import dayjs from 'dayjs';
import {
  CARD,
  CC_READER_TYPE,
  consoleWarningStyle,
  IMAGE_ORIENTATIONS,
} from './constants';
/*
 * @param {string} action
 * @param {string} item
 * @param {object} abilities - the currentAbilities object returned from
 * the API
 * example: can('show', 'reporting_navigation_link', currentAbilities)
 */
export const can = (action, item, abilities) =>
  abilities?.[action]?.includes(item) ?? false;

export const formatCurrency = (rawPrice) =>
  parseFloat(rawPrice)
    .toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    })
    .replace('$', '');

export const formatCurrencyInput = (rawPrice) =>
  parseFloat(rawPrice).toFixed(2);

export const formatPrice = (rawPrice, negative = false) =>
  `${negative ? '-' : ''}$${formatCurrency(rawPrice)}`;

export const formatWithParentheses = (value) => {
  return `(${value})`;
};

export const removeLeadingMinus = (value) => {
  if (value === null || value === undefined || typeof value == 'boolean')
    return '';
  const stringValue = value.toString();
  return stringValue.startsWith('-') ? stringValue.slice(1) : stringValue;
};

export const capitalize = (string) =>
  string?.charAt(0)?.toUpperCase() + string?.slice(1) ?? string;

export const formatDate = (timestamp) =>
  new Date(timestamp)?.toLocaleString('default')?.split(',')?.join(' - ');

export const camelCaseToSnakeCase = (string) =>
  string.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

export const denominationMultipliers = [
  1, 5, 10, 25, 100, 200, 500, 1000, 2000, 5000, 10000,
];

const MM_DD_YYYY_FORMAT_LENGTH = 8;

const dateInputToISOString = (dateString = '') => {
  if (dateString.length < MM_DD_YYYY_FORMAT_LENGTH) return '';

  const month = dateString.slice(0, 2);
  const day = dateString.slice(2, 4);
  const year = dateString.slice(4, 8);

  return new Date(`${year}-${month}-${day}`).toISOString();
};

export const delimitDateString = (dateString = '') =>
  dateString.length === MM_DD_YYYY_FORMAT_LENGTH
    ? `${dateString.substring(0, 2)}/${dateString.substring(
        2,
        4,
      )}/${dateString.substring(4, 8)}`
    : '';

// Utility function to convert MMDDYYYY date format to ISO YYYY-MM-DD format
export const convertToISOFormat = (mmddyyyyDate) => {
  if (!mmddyyyyDate) return '';
  const formatted = delimitDateString(mmddyyyyDate);
  return dayjs(formatted).format('YYYY-MM-DD');
};

export const DATE_FIELDS = ['startDate', 'endDate'];
export const getFiltersFromFields = (filterFields) =>
  Object.entries(filterFields)
    .filter(([_, { rawValue }]) => Boolean(rawValue))
    .map(([field, { rawValue }]) => {
      if (DATE_FIELDS.includes(field)) {
        return `filters[${camelCaseToSnakeCase(field)}]=${dateInputToISOString(
          rawValue,
        )}`;
      }
      return `filters[${camelCaseToSnakeCase(field)}]=${rawValue}`;
    })
    .join('&');

// Capitalize the first character of the string, then replace all instances of
// "_something" with a space and a capitalized version of whatever followed
// the underscore. Ex: "my_form_field" will become "My_form_field" and then
// that will be further transformed into "My Form Field".
// See Also: https://stackoverflow.com/a/64489760
export const titleCase = (s) =>
  (s ?? '')
    .replace(/^[-_]*(.)/, (_, c) => c.toUpperCase())
    .replace(/[-_]+(.)/g, (_, c) => ' ' + c.toUpperCase());

export const genRandomString = (text) => `${text}${Math.random()}`; // used to create unique React key prop https://reactjs.org/docs/lists-and-keys.html

export const isEmptyObj = (obj) => Object.keys(obj).length === 0;

export const isDatacapTransaction = (payment_tender_type, cc_reader_type) =>
  (cc_reader_type === CC_READER_TYPE.TRANCLOUD ||
    cc_reader_type === CC_READER_TYPE.DC_DIRECT) &&
  payment_tender_type === CARD;

export const downloadReport = (type, terminal, date, url) => {
  const document_name = `${terminal.replace(/\s+/g, '-')}-${type}-${dayjs(
    date,
  ).format('YYYYMMDDHHmmss')}`;
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.href = url;
  a.download = document_name;
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const debounce = (callback, delay = 1000) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      callback(...args);
    }, delay);
  };
};

export const getImageOrientation = async (imageUrl) => {
  try {
    const imageObjectUrl = await fetch(imageUrl)
      .then((response) => response.blob())
      .then((imageBlob) => URL.createObjectURL(imageBlob));

    const image = document.createElement('img');
    image.src = imageObjectUrl;

    await image.decode();

    const portraitOrLandscapeImage =
      image.width > image.height
        ? IMAGE_ORIENTATIONS.LANDSCAPE
        : IMAGE_ORIENTATIONS.PORTRAIT;
    return portraitOrLandscapeImage;
  } catch (err) {
    console.warn(
      `%cWarning: getImageOrientation error: ${err}`,
      `background-color: ${consoleWarningStyle}`,
    );
  }
};

export const phoneNumberWithoutCountryCode = (phoneNumber) => {
  const countryCodeRegex = /^\+?\d{1}/;
  const hasCountryCode = countryCodeRegex.test(phoneNumber);
  const numberRegex = /[\s()-]*\D+/g;
  const cleanedNumber = phoneNumber.replace(numberRegex, '');

  if (hasCountryCode && cleanedNumber.length === 11) {
    const numberWithoutCountryCode = cleanedNumber.replace(
      countryCodeRegex,
      '',
    );
    return numberWithoutCountryCode;
  }

  return cleanedNumber;
};

/**
 * Method to change other field values based on the just-changed value of a field
 * with 'replace' transforms.
 * @param {object} formData - Contains initial formData object prior to transformations (should contain updated value of changedField)
 * @param {object} changedField - The `root_` name of the field that was changed
 * @param {object} overrides - Overrides to apply to other fields in the same form that are based on the new value of the changed field
 * @returns object - Contains the transformed form field data
 */
export const transformReplaceFormData = (formData, changedField, overrides) => {
  const OVERRIDE_TYPE = 'replace';
  if (
    !changedField ||
    !overrides ||
    !overrides?.hasOwnProperty(OVERRIDE_TYPE)
  ) {
    return formData;
  }
  const changedKey = changedField?.split?.('root_')?.[1];
  const isTransformApplicable =
    overrides[OVERRIDE_TYPE].hasOwnProperty(changedKey);

  if (isTransformApplicable) {
    const transforms = overrides[OVERRIDE_TYPE][changedKey];
    const trigger = formData[changedKey];
    const replacements = transforms.filter((t) => t.trigger === trigger);
    replacements.forEach((t) => {
      formData[t.field] = t.value;
    });
  }
  return formData;
};

export const getNamespace = (hostname) => {
  if (!hostname || typeof hostname !== 'string' || hostname === 'localhost') {
    return null;
  }

  if (hostname === 'pos.thecitybase.com') return 'default';

  const matches = hostname.match(/^pos-frontend-([\w\-]*)\./);

  return matches?.[1] ?? null;
};
